import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useRBAC } from '@/hooks/useRBAC';
import { initPromise } from '@/i18n';
import { getAdminSubscriptions, deleteAdminSubscription, sendNewsletter, getNewsletterHistory, getNewsletterHistoryById } from '@/services/admin/subscriptions.admin.api';
import type { AdminSubscription, NewsletterHistory, NewsletterHistoryResponse } from '@/services/admin/subscriptions.admin.api';
import AlertBox from '@/components/common/AlertBox';

type ActiveTab = 'subscriptions' | 'history';
type AlertType = 'success' | 'error' | 'warning' | 'info';

export default function SubscriptionsPage() {
  const { t } = useTranslation();
  const { can } = useRBAC();
  const [isReady, setIsReady] = useState(false);
  const [activeTab, setActiveTab] = useState<ActiveTab>('subscriptions');
  const [subscriptions, setSubscriptions] = useState<AdminSubscription[]>([]);
  const [history, setHistory] = useState<NewsletterHistory[]>([]);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyPage, setHistoryPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [searchEmail, setSearchEmail] = useState('');
  const [showNewsletterModal, setShowNewsletterModal] = useState(false);
  const [newsletterForm, setNewsletterForm] = useState({ subject: '', content: '' });
  const [sendingEmail, setSendingEmail] = useState(false);
  const [alert, setAlert] = useState<{ show: boolean; type: AlertType; message: string }>({
    show: false,
    type: 'info',
    message: '',
  });
  const [confirmAlert, setConfirmAlert] = useState<{ show: boolean; message: string; onConfirm: () => void }>({
    show: false,
    message: '',
    onConfirm: () => {},
  });

  // 等待 i18n 初始化
  useEffect(() => {
    initPromise
      .then(() => {
        console.log('✅ SubscriptionsPage: i18n initialized');
        setIsReady(true);
      })
      .catch((error) => {
        console.error('❌ SubscriptionsPage: i18n init failed:', error);
        setIsReady(true);
      });
    
    const timeout = setTimeout(() => {
      console.log('⏰ SubscriptionsPage: i18n timeout fallback');
      setIsReady(true);
    }, 5000);
    
    return () => clearTimeout(timeout);
  }, []);

  // 加载订阅列表
  useEffect(() => {
    if (isReady && activeTab === 'subscriptions') {
      loadSubscriptions();
    }
  }, [statusFilter, isReady, activeTab]);

  // 加载邮件发送历史
  useEffect(() => {
    if (isReady && activeTab === 'history') {
      loadHistory();
    }
  }, [isReady, activeTab, historyPage]);

  const loadSubscriptions = async () => {
    setLoading(true);
    try {
      console.log('📡 正在加载订阅列表...', { statusFilter });
      const data = statusFilter === 'all' 
        ? await getAdminSubscriptions()
        : await getAdminSubscriptions(statusFilter);
      console.log('✅ 订阅列表加载成功:', data);
      setSubscriptions(data);
    } catch (error) {
      console.error('❌ 加载订阅列表失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadHistory = async () => {
    setLoading(true);
    try {
      console.log('📡 正在加载邮件发送历史...', { page: historyPage });
      const response: NewsletterHistoryResponse = await getNewsletterHistory(historyPage, 10);
      console.log('✅ 邮件发送历史加载成功:', response);
      setHistory(response.items);
      setHistoryTotal(response.total);
    } catch (error) {
      console.error('❌ 加载邮件发送历史失败:', error);
    } finally {
      setLoading(false);
    }
  };

  // 过滤订阅
  const filteredSubscriptions = (subscriptions || []).filter(sub => 
    sub.email.toLowerCase().includes(searchEmail.toLowerCase())
  );

  // 删除订阅
  const handleDelete = async (id: number) => {
    setConfirmAlert({
      show: true,
      message: t('admin.subscriptions.confirmDelete'),
      onConfirm: async () => {
        try {
          await deleteAdminSubscription(id);
          await loadSubscriptions();
          setAlert({ show: true, type: 'success', message: t('admin.subscriptions.deleteSuccess') });
        } catch (error) {
          console.error('删除订阅失败:', error);
          setAlert({ show: true, type: 'error', message: t('admin.subscriptions.deleteFailed') });
        }
      },
    });
  };

  // 发送新闻邮件
  const handleSendNewsletter = async () => {
    if (!newsletterForm.subject || !newsletterForm.content) {
      setAlert({ show: true, type: 'warning', message: t('admin.subscriptions.fillAllFields') });
      return;
    }

    setSendingEmail(true);
    try {
      await sendNewsletter(newsletterForm.subject, newsletterForm.content);
      setAlert({ show: true, type: 'success', message: t('admin.subscriptions.emailQueued') });
      setShowNewsletterModal(false);
      setNewsletterForm({ subject: '', content: '' });
      
      // 切换到历史标签页并刷新
      setActiveTab('history');
      await loadHistory();
    } catch (error) {
      console.error('发送邮件失败:', error);
      setAlert({ show: true, type: 'error', message: t('admin.subscriptions.sendFailed') });
    } finally {
      setSendingEmail(false);
    }
  };

  // 格式化日期
  const formatDate = (dateStr: string) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('zh-CN');
  };

  // 统计
  const stats = {
    total: (subscriptions || []).length,
    active: (subscriptions || []).filter(s => s.status === 'active').length,
    unsubscribed: (subscriptions || []).filter(s => s.status === 'unsubscribed').length,
  };

  if (!isReady) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-gray-500">加载中...</div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* 页面标题 */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">
          {t('admin.subscriptions.title')}
        </h1>
        <p className="text-gray-600 mt-1">
          {t('admin.subscriptions.description')}
        </p>
      </div>

      {/* 统计卡片 */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-2xl font-bold text-blue-600">{stats.total}</div>
          <div className="text-sm text-gray-600">{t('admin.subscriptions.total')}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-2xl font-bold text-green-600">{stats.active}</div>
          <div className="text-sm text-gray-600">{t('admin.subscriptions.active')}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-2xl font-bold text-gray-600">{stats.unsubscribed}</div>
          <div className="text-sm text-gray-600">{t('admin.subscriptions.unsubscribed')}</div>
        </div>
      </div>

      {/* 标签页切换 */}
      <div className="bg-white rounded-lg shadow mb-6">
        <div className="border-b border-gray-200">
          <nav className="flex -mb-px">
            <button
              onClick={() => setActiveTab('subscriptions')}
              className={`px-6 py-3 border-b-2 font-medium text-sm ${
                activeTab === 'subscriptions'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {t('admin.subscriptions.tabSubscriptions')}
            </button>
            <button
              onClick={() => setActiveTab('history')}
              className={`px-6 py-3 border-b-2 font-medium text-sm ${
                activeTab === 'history'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {t('admin.subscriptions.tabHistory')}
            </button>
          </nav>
        </div>
      </div>

      {activeTab === 'subscriptions' && (
      <>
      {/* 操作栏 */}
      <div className="bg-white rounded-lg shadow mb-6 p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            {/* 状态筛选 */}
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">{t('admin.subscriptions.filterAll')}</option>
              <option value="active">{t('admin.subscriptions.filterActive')}</option>
              <option value="unsubscribed">{t('admin.subscriptions.filterUnsubscribed')}</option>
            </select>

            {/* 邮箱搜索 */}
            <input
              type="text"
              placeholder={t('admin.subscriptions.searchPlaceholder')}
              value={searchEmail}
              onChange={(e) => setSearchEmail(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-2 w-64 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* 发送新闻邮件按钮 */}
          {can('subscriptions:write') && (
            <button
              onClick={() => setShowNewsletterModal(true)}
              className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 flex items-center"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              {t('admin.subscriptions.sendNewsletter')}
            </button>
          )}
        </div>
      </div>

      {/* 订阅列表 */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-500">
            {t('admin.common.loading')}
          </div>
        ) : filteredSubscriptions.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            {t('admin.subscriptions.noData')}
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('admin.subscriptions.email')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('admin.subscriptions.status')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('admin.subscriptions.source')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('admin.subscriptions.subscribedAt')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('admin.subscriptions.unsubscribedAt')}
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('admin.subscriptions.actions')}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredSubscriptions.map((subscription) => (
                <tr key={subscription.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{subscription.email}</div>
                    {subscription.name && (
                      <div className="text-sm text-gray-500">{subscription.name}</div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      subscription.status === 'active'
                        ? 'bg-green-100 text-green-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {subscription.status === 'active' 
                        ? t('admin.subscriptions.statusActive')
                        : t('admin.subscriptions.statusUnsubscribed')}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {subscription.source || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(subscription.subscribed_at)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(subscription.unsubscribed_at)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    {can('subscriptions:delete') && (
                      <button
                        onClick={() => handleDelete(subscription.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        {t('admin.common.delete')}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      </>
      )}

      {activeTab === 'history' && (
      <>
      {/* 邮件发送历史 */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-500">
            {t('admin.common.loading')}
          </div>
        ) : history.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            {t('admin.subscriptions.historyNoData')}
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('admin.subscriptions.historySubject')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('admin.subscriptions.historyRecipientCount')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('admin.subscriptions.historyStatus')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('admin.subscriptions.historyCreatedBy')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('admin.subscriptions.historyCreatedAt')}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {history.map((item) => (
                <tr key={item.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="text-sm font-medium text-gray-900">{item.subject}</div>
                    <div className="text-sm text-gray-500 truncate max-w-xs">
                      {item.content.substring(0, 50)}...
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {item.success_count}/{item.recipient_count}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      item.status === 'completed'
                        ? 'bg-green-100 text-green-800'
                        : item.status === 'sending'
                        ? 'bg-yellow-100 text-yellow-800'
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {t(`admin.subscriptions.historyStatus_${item.status}`)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {item.created_by}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(item.created_at)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        
        {/* 分页 */}
        {historyTotal > 10 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
            <div className="flex-1 flex justify-between items-center">
              <div className="text-sm text-gray-700">
                {t('admin.common.pageInfo', { page: historyPage, total: Math.ceil(historyTotal / 10) })}
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => setHistoryPage(p => Math.max(1, p - 1))}
                  disabled={historyPage === 1}
                  className="px-3 py-1 border border-gray-300 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  {t('admin.common.prevPage')}
                </button>
                <button
                  onClick={() => setHistoryPage(p => p + 1)}
                  disabled={historyPage >= Math.ceil(historyTotal / 10)}
                  className="px-3 py-1 border border-gray-300 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  {t('admin.common.nextPage')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      </>
      )}

      {/* 发送新闻邮件弹窗 */}
      {showNewsletterModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-2xl">
            <h3 className="text-xl font-bold mb-4">
              {t('admin.subscriptions.sendNewsletter')}
            </h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('admin.subscriptions.emailSubject')}
                </label>
                <input
                  type="text"
                  value={newsletterForm.subject}
                  onChange={(e) => setNewsletterForm({ ...newsletterForm, subject: e.target.value })}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder={t('admin.subscriptions.subjectPlaceholder')}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('admin.subscriptions.emailContent')}
                </label>
                <textarea
                  value={newsletterForm.content}
                  onChange={(e) => setNewsletterForm({ ...newsletterForm, content: e.target.value })}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 h-64 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder={t('admin.subscriptions.contentPlaceholder')}
                />
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                <p className="text-sm text-blue-800">
                  {t('admin.subscriptions.recipientInfo', { count: stats.active })}
                </p>
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setShowNewsletterModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                {t('admin.common.cancel')}
              </button>
              <button
                onClick={handleSendNewsletter}
                disabled={sendingEmail}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400"
              >
                {sendingEmail ? t('admin.common.sending') : t('admin.subscriptions.send')}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Alert Box */}
      <AlertBox
        type={alert.type}
        message={alert.message}
        isOpen={alert.show}
        onClose={() => setAlert({ ...alert, show: false })}
        autoClose={true}
        autoCloseDelay={3000}
      />
      {/* Confirm Box */}
      <AlertBox
        type="warning"
        message={confirmAlert.message}
        isOpen={confirmAlert.show}
        onClose={() => setConfirmAlert({ ...confirmAlert, show: false })}
        showCancel
        onCancel={() => setConfirmAlert({ ...confirmAlert, show: false })}
        onConfirm={() => {
          confirmAlert.onConfirm();
          setConfirmAlert({ ...confirmAlert, show: false });
        }}
        confirmText={t('admin.common.confirm')}
        cancelText={t('admin.common.cancel')}
      />
    </div>
  );
}
